import type { ApiSchema } from "../../../api/apiSchema";
import type { CapabilityValueType } from "../../../api/content/authoring/capabilityVocabularyApi";

/**
 * Drafts of `capability_overrides` items (capabilities.md CAP-4): a project's reviewed
 * assertions about what models can do. The go-live dialog opens the form with one entry
 * filled in for a capability DMW does not know; the query names it.
 */

export type CapabilityOverridesContent = ApiSchema<"CapabilityOverridesContent">;
export type CapabilityOverrideEntry = ApiSchema<"CapabilityOverrideEntry">;
export type OverrideValue = CapabilityOverrideEntry["value"];

export interface OverridePrefill {
  readonly routeKind: CapabilityOverrideEntry["route_kind"];
  /** The agent's own name or the API's host, as the route options name it. */
  readonly routeMatch: string;
  /** A model ID, or `*` for an agent's default model. */
  readonly modelMatch: string;
  readonly key: string;
  readonly value: OverrideValue;
}

const QUERY = { routeKind: "route_kind", routeMatch: "route_match", modelMatch: "model_match", key: "key", value: "value" } as const;

export const NEW_OVERRIDES: CapabilityOverridesContent = { kind: "capability_overrides", name: "", description: "", entries: [] };

export function emptyEntry(): CapabilityOverrideEntry {
  return { route_kind: "agent", route_match: "*", model_match: "*", key: "", value: true, note: "" };
}

/** The query string that opens the form with `prefill` as its first entry. */
export function prefillQuery(prefill: OverridePrefill): string {
  return new URLSearchParams({
    [QUERY.routeKind]: prefill.routeKind,
    [QUERY.routeMatch]: prefill.routeMatch,
    [QUERY.modelMatch]: prefill.modelMatch,
    [QUERY.key]: prefill.key,
    [QUERY.value]: JSON.stringify(prefill.value),
  }).toString();
}

/** The draft a query opens: one entry from it, or the empty draft. */
export function draftOfQuery(query: URLSearchParams): CapabilityOverridesContent {
  const routeKind = query.get(QUERY.routeKind);
  const key = query.get(QUERY.key);
  if ((routeKind !== "agent" && routeKind !== "api_key") || key === null || key === "") {
    return NEW_OVERRIDES;
  }
  return {
    ...NEW_OVERRIDES,
    entries: [
      {
        route_kind: routeKind,
        route_match: query.get(QUERY.routeMatch) ?? "*",
        model_match: query.get(QUERY.modelMatch) ?? "*",
        key,
        value: parsedValue(query.get(QUERY.value)),
        note: "",
      },
    ],
  };
}

/** The value a typed text stands for, for a key of `type`. */
export function valueOfText(type: CapabilityValueType, text: string): OverrideValue {
  switch (type) {
    case "boolean":
      return text === "true";
    case "integer": {
      const parsed = Number.parseInt(text, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    case "string":
      return text;
  }
}

/** The value's default when an entry's key changes to one of `type`. */
export function defaultValueOf(type: CapabilityValueType): OverrideValue {
  switch (type) {
    case "boolean":
      return true;
    case "integer":
      return 0;
    case "string":
      return "";
  }
}

function parsedValue(text: string | null): OverrideValue {
  if (text === null) {
    return true;
  }
  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === "boolean" || typeof parsed === "number" || typeof parsed === "string" ? parsed : true;
  } catch {
    return true;
  }
}
